<template>
  <div v-if="isScientificTheme">
    <div class="select-scientific-theme-wrapper">
      <div class="select-scientific-title">
        {{ $t("agenda.filters_title") }}
      </div>
      <div
        v-for="(item, index) in filteredItems"
        :key="index"
        :class="{ 'select-scientific': index >= 0 }"
        class="unselectable"
        @click="togglePath(index)"
      >
        <template v-if="item.id >= 0">
          <checkbox :value="isSelected(index)" @input="togglePath(index)" size="22px"></checkbox>
          <div class="select-scientific-label">
            <span class="notranslate" style="margin-right: 4px">{{ item.value }}</span>
          </div>
          <div class="select-scientific-blob" :style="{ backgroundColor: item.hex }"></div>
        </template>
        <template v-else>
          <div class="is-size-7 has-text-weight-bold">{{ item.value }}</div>
        </template>
      </div>
    </div>
    <div class="select-scientific-paginated" :class="{ 'is-scientific': this.isScientificTheme }">
      <dropdown-select :items="filteredItems" :multiple="true" :value="selectedIndices" @input="onSelectedIndices">
        <!-- tag items -->
        <template #dropdown="{ item }">
          <div class="mr-1" v-if="item.hex">
            <point-icon class="point-icon" :style="{ fill: item.hex }"></point-icon>
            <span>{{ item.value.toUpperCase() }}</span>
          </div>
          <div class="mr-1" v-else>
            <div class="is-size-5 has-text-centered">{{ item.value.toUpperCase() }}</div>
          </div>
        </template>

        <template #trigger="{ item }">
          <div class="mr-1" v-if="!!item.hex">
            <point-icon class="point-icon" :style="{ fill: item.hex }"></point-icon>
            <span>{{ item.value.toUpperCase() }}</span>
          </div>
          <div class="mr-1" v-else>
            <div class="is-size-5 has-text-centered">{{ item.value.toUpperCase() }}</div>
          </div>
        </template>

        <!-- Placeholder text -->
        <template #placeholder>
          <span :class="{ 'text-dark': filterTextDark }">{{ $t("agenda.filter_by_paths") }}</span>
        </template>
      </dropdown-select>
    </div>
  </div>
  <dropdown-select :items="filteredItems" :multiple="true" :value="selectedIndices" @input="onSelectedIndices" v-else>
    <!-- tag items -->
    <template #dropdown="{ item }">
      <div class="mr-1" v-if="!!item.hex">
        <point-icon class="point-icon" :style="{ fill: item.hex }"></point-icon>
        <span :class="{ 'text-dark': filterTextDark }">{{ item.value.toUpperCase() }}</span>
      </div>
      <div class="mr-1" v-else>
        <div class="is-size-5 has-text-centered">{{ item.value.toUpperCase() }}</div>
      </div>
    </template>

    <template #trigger="{ item }">
      <div class="mr-1" v-if="!!item.hex">
        <point-icon class="point-icon" :style="{ fill: item.hex }"></point-icon>
        <span :class="{ 'text-dark': filterTextDark }">{{ item.value.toUpperCase() }}</span>
      </div>
      <div class="mr-1" v-else>
        <div class="is-size-5 has-text-centered">{{ item.value.toUpperCase() }}</div>
      </div>
    </template>

    <!-- Placeholder text -->
    <template #placeholder>
      <span :class="{ 'text-dark': filterTextDark }">{{ $t("agenda.filter_by_paths") }}</span>
    </template>
  </dropdown-select>
</template>

<script>
import { mapMutations, mapGetters, mapState } from "vuex";
import PointIcon from "@/assets/icons/point.svg";
import DropdownSelect from "@/shared/components/DropdownSelect";
import Constants from "@/widgets/shared/constants";
import Checkbox from "@/shared/components/form/Checkbox";

export default {
  name: "FiltersSelect",

  components: {
    Checkbox,
    PointIcon,
    DropdownSelect,
  },

  props: ["hidePaths"],

  mounted() {
    // Not sure why it was here, but I commented it out for preselectedPathIds/preselectedPlaceIds to work correctly
    // this.selectPathsByIndex([]);
    // this.selectPlacesByIndex([]);
  },

  methods: {
    ...mapMutations("paths", { selectPathsByIndex: "selectByIndex" }),
    ...mapMutations("places", { selectPlacesByIndex: "selectByIndex" }),

    onSelectedIndices(indices) {
      let indicesWithoutLabels = indices.filter(it => it !== 0 && it !== this.placesLabelIndex);
      let pathsIndices = indicesWithoutLabels.filter(it => it < this.placesLabelIndex).map(it => it - 1);
      let placesIndices = indicesWithoutLabels.filter(it => it > this.placesLabelIndex).map(it => it - this.placesLabelIndex - 1);
      this.selectPathsByIndex(pathsIndices);
      this.selectPlacesByIndex(placesIndices);
      this.$emit("path-selected");
    },

    togglePath(index) {
      if (index !== 0 && index !== this.placesLabelIndex) {
        if (this.selectedIndices.includes(index)) {
          if (index < this.placesLabelIndex) {
            let pathIndices = this.selectedIndices.filter(it => it < this.placesLabelIndex);
            let arrayIndex = pathIndices.indexOf(index);
            pathIndices.splice(arrayIndex, 1);
            let result = pathIndices.map(it => it - 1);
            this.selectPathsByIndex(result);
          } else {
            let placesIndices = this.selectedIndices.filter(it => it > this.placesLabelIndex);
            let arrayIndex = placesIndices.indexOf(index);
            placesIndices.splice(arrayIndex, 1);
            let result = placesIndices.map(it => it - this.placesLabelIndex - 1);
            this.selectPlacesByIndex(result);
          }
        } else {
          if (index < this.placesLabelIndex) {
            let pathIndices = this.selectedIndices.filter(it => it < this.placesLabelIndex);
            let result = pathIndices.map(it => it - 1);
            this.selectPathsByIndex([...result, index - 1]);
          } else {
            let placesIndices = this.selectedIndices.filter(it => it > this.placesLabelIndex);
            let result = placesIndices.map(it => it - this.placesLabelIndex - 1);
            this.selectPlacesByIndex([...result, index - this.placesLabelIndex - 1]);
          }
        }
        this.$emit("path-selected");
      }
    },

    isSelected(index) {
      let placesLabelIndex = this.placesLabelIndex;
      if (index > placesLabelIndex) {
        //place item
        let trueIndex = index - placesLabelIndex - 1;
        return this.selectedPlacesIndices.includes(trueIndex);
      } else if (index > 0 && index < placesLabelIndex) {
        //path item
        let trueIndex = index - 1;
        return this.selectedPathsIndices.includes(trueIndex);
      } else {
        //item is a label, so it can't be selected
        return false;
      }
    },
  },

  computed: {
    ...mapGetters("paths", ["filteredPaths"]),
    ...mapGetters("places", ["filteredPlaces"]),
    ...mapState(["theme"]),
    ...mapState("paths", { selectedPathsIndices: "selectedIndexes" }),
    ...mapState("places", { selectedPlacesIndices: "selectedIndexes" }),
    ...mapState(["eventId"]),

    isScientificTheme() {
      return this.theme === Constants.WIDGET_THEME_TYPES.SCIENTIFIC;
    },

    filterTextDark() {
      return this.eventId === 1419 || this.eventId === 118; // PED event
    },

    filteredItems() {
      if (this.hidePaths) {
        return [
          { id: -2, value: this.$t("agenda.places") },
          ...this.filteredPlaces,
        ];
      } else {
        return [
          { id: -1, value: this.$t("agenda.paths") },
          ...this.filteredPaths,
          { id: -2, value: this.$t("agenda.places") },
          ...this.filteredPlaces,
        ];
      }
    },

    selectedIndices() {
      return [...this.selectedPathsIndices.map(it => it + 1), ...this.selectedPlacesIndices.map(it => it + this.placesLabelIndex + 1)];
    },

    placesLabelIndex() {
      return this.filteredItems.findIndex(item => item.id === -2);
    },
  },
};
</script>

<style lang="scss" scoped>
.point-icon {
  margin: -4px 5px 0 0;
  vertical-align: middle;
}

.text-dark {
  color: #d3d3d3 !important;
}
</style>
